import type { Baustein, Catalog, Skill } from "./types"

/**
 * Lookup tables derived from a catalog, built once per catalog object.
 *
 * The engine rebuilt these constantly: `expandPrerequisites` constructed a
 * 51-entry `Map` on **every recursive call**, and `resolveCurrentLevels` /
 * `computeGaps` / `composeCurriculum` each built their own per employee. Over
 * the 1,960-person population that is tens of thousands of Map constructions per
 * request, all of them identical.
 *
 * This is the ONLY cache that belongs inside `engine/`. It is keyed on the
 * caller's own catalog object and holds nothing about any employee, so the
 * module stays pure in the sense that matters: no request-scoped state, nothing
 * that could leak one person's data into another's plan, and nothing that stops
 * the engine being lifted onto SAP BTP as-is. A WeakMap means a catalog that
 * goes out of scope takes its index with it.
 */
export interface CatalogIndex {
    bausteinById: ReadonlyMap<string, Baustein>
    skillById: ReadonlyMap<string, Skill>
    /** Skills at least one Baustein teaches — the "is this even trainable?" set. */
    teachableSkills: ReadonlySet<string>
}

const CACHE = new WeakMap<Catalog, CatalogIndex>()

export function catalogIndex(catalog: Catalog): CatalogIndex {
    const cached = CACHE.get(catalog)
    if (cached) return cached

    const index: CatalogIndex = {
        bausteinById: new Map(catalog.bausteine.map((baustein) => [baustein.id, baustein])),
        skillById: new Map(catalog.skills.map((skill) => [skill.id, skill])),
        teachableSkills: new Set(
            catalog.bausteine.flatMap((baustein) => baustein.teaches.map((t) => t.skillId)),
        ),
    }
    CACHE.set(catalog, index)
    return index
}
